import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { addBinome, getContacts, getLienBinomes, setAuthToken } from '../services/api';
import { toast } from 'react-toastify';
import './BinomeForm.css';

function BinomeForm() {
  const { id } = useParams(); // contact id
  const navigate = useNavigate();
  const auth = useAuth();

  const [contacts, setContacts] = useState([]);
  const [lienBinomes, setLienBinomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [formData, setFormData] = useState({
    binome_id: '',
    lien_binome_id: '',
    precisions: ''
  });

  useEffect(() => {
    if (auth.user?.access_token) {
      setAuthToken(auth.user.access_token);
      loadData();
    }
  }, [auth.user]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [contactsData, lienBinomesData] = await Promise.all([
        getContacts({ limit: 500 }),
        getLienBinomes()
      ]);
      // Filter out the current contact
      const filteredContacts = (contactsData.data || []).filter(c => c.id !== parseInt(id));
      setContacts(filteredContacts);
      setLienBinomes(lienBinomesData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement des donnees');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.binome_id) {
      toast.error('Veuillez selectionner un contact');
      return;
    }

    if (!formData.lien_binome_id) {
      toast.error('Veuillez selectionner un type de lien');
      return;
    }

    try {
      setSubmitting(true);
      await addBinome(id, formData);
      toast.success('Binome ajoute avec succes');
      navigate(`/contacts/${id}`);
    } catch (error) {
      console.error('Error adding binome:', error);
      toast.error('Erreur lors de l\'ajout du binome');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredContacts = contacts.filter(c => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${c.prenom || ''} ${c.nom || ''}`.toLowerCase();
    const ville = (c.antenne_ville || c.ville || '').toLowerCase();
    return fullName.includes(searchLower) || ville.includes(searchLower);
  });

  if (loading) {
    return (
      <div className="binome-form-page">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          Chargement...
        </div>
      </div>
    );
  }

  return (
    <div className="binome-form-page">
      <header className="page-header">
        <div className="header-info">
          <Link to={`/contacts/${id}`} className="back-link">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 18l-6-6 6-6" />
            </svg>
            Retour au contact
          </Link>
          <h1>Ajouter un binome</h1>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="binome-form">
        <div className="form-section">
          <h3>Selectionner un contact comme binome</h3>

          <div className="search-input-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Rechercher un contact..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="contacts-list-select">
            {filteredContacts.length === 0 ? (
              <p className="no-results">Aucun contact trouve</p>
            ) : (
              filteredContacts.slice(0, 50).map(contact => (
                <label
                  key={contact.id}
                  className={`contact-option ${formData.binome_id === contact.id ? 'selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="binome_id"
                    value={contact.id}
                    checked={formData.binome_id === contact.id}
                    onChange={() => setFormData({ ...formData, binome_id: contact.id })}
                  />
                  <div className="contact-option-content">
                    <span className="contact-option-name">
                      {contact.prenom} {contact.nom}
                    </span>
                    <span className="contact-option-location">
                      {contact.antenne_ville || contact.ville || 'Non renseigne'}
                    </span>
                  </div>
                </label>
              ))
            )}
            {filteredContacts.length > 50 && (
              <p className="more-results">
                Et {filteredContacts.length - 50} autres contacts. Affinez votre recherche.
              </p>
            )}
          </div>
        </div>

        <div className="form-section">
          <h3>Type de lien</h3>
          <select
            value={formData.lien_binome_id}
            onChange={(e) => setFormData({ ...formData, lien_binome_id: e.target.value })}
            className="form-select"
            required
          >
            <option value="">Selectionner un type de lien</option>
            {lienBinomes.map(lien => (
              <option key={lien.id} value={lien.id}>{lien.nom || lien.valeur}</option>
            ))}
          </select>
        </div>

        <div className="form-section">
          <h3>Precisions (optionnel)</h3>
          <textarea
            value={formData.precisions}
            onChange={(e) => setFormData({ ...formData, precisions: e.target.value })}
            className="form-textarea"
            rows={4}
            placeholder="Informations complementaires sur cette relation..."
          />
        </div>

        <div className="form-actions">
          <Link to={`/contacts/${id}`} className="btn btn-secondary">
            Annuler
          </Link>
          <button type="submit" disabled={submitting} className="btn btn-primary">
            {submitting ? 'Enregistrement...' : 'Ajouter le binome'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default BinomeForm;
